/**
 * A very small IndexedDB wrapper.
 *
 * Deliberately hand-written rather than a library: the whole surface we need is
 * get/put/delete/list over three object stores, and a dependency that ships a
 * query language would be an order of magnitude more code than this file.
 *
 * Every operation resolves rather than rejects on failure. IndexedDB is
 * unavailable in private windows on some browsers, can be evicted under storage
 * pressure, and throws on quota — none of which is a reason for the app to stop
 * working. Offline storage is an enhancement; if it is gone, the app is simply
 * online-only again.
 */

const DB_NAME = 'travessa-offline';
const DB_VERSION = 2;

/**
 * Snapshots saved by the old «Сохранить офлайн» button, keyed by trip id.
 * Read once to move them into `queries` (`persist.ts`), then left empty.
 */
export const STORE_TRIPS = 'trips';
/** Small singletons: the signed-in user, each trip's media progress. */
export const STORE_META = 'meta';
/** Persisted query results, keyed by the query's hash (`persist.ts`). */
export const STORE_QUERIES = 'queries';

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_TRIPS)) db.createObjectStore(STORE_TRIPS);
      if (!db.objectStoreNames.contains(STORE_META)) db.createObjectStore(STORE_META);
      if (!db.objectStoreNames.contains(STORE_QUERIES)) db.createObjectStore(STORE_QUERIES);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });

  return dbPromise;
}

function run<T>(
  storeName: string,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  return openDb().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) {
          resolve(null);
          return;
        }
        try {
          const tx = db.transaction(storeName, mode);
          const request = action(tx.objectStore(storeName));
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => resolve(null);
          // A quota error surfaces on the transaction, not the request.
          tx.onabort = () => resolve(null);
        } catch {
          resolve(null);
        }
      }),
  );
}

export function idbGet<T>(store: string, key: string): Promise<T | null> {
  return run<T>(store, 'readonly', (s) => s.get(key) as IDBRequest<T>);
}

export function idbPut(store: string, key: string, value: unknown): Promise<unknown> {
  return run(store, 'readwrite', (s) => s.put(value, key) as IDBRequest<unknown>);
}

export function idbDelete(store: string, key: string): Promise<unknown> {
  return run(store, 'readwrite', (s) => s.delete(key) as IDBRequest<undefined>);
}

export function idbKeys(store: string): Promise<string[]> {
  return run<IDBValidKey[]>(store, 'readonly', (s) => s.getAllKeys()).then(
    (keys) => (keys ?? []).map(String),
  );
}

export function idbValues<T>(store: string): Promise<T[]> {
  return run<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>).then((v) => v ?? []);
}

export function idbClear(store: string): Promise<unknown> {
  return run(store, 'readwrite', (s) => s.clear() as IDBRequest<undefined>);
}

/** Drop everything. Called on logout — offline data must not outlive a session. */
export async function idbWipe(): Promise<void> {
  await Promise.all([idbClear(STORE_TRIPS), idbClear(STORE_META), idbClear(STORE_QUERIES)]);
}
